import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * "Add this photo to Newsdesk Stock" — the opt-in shown next to the
 * article page's image uploads (featured image + body-image Upload tab).
 *
 * Tick it BEFORE uploading or pasting: the upload then also sends the
 * full-resolution file to the library, LIVE straight away, recording
 * which article image it came from (drives the "In Stock" marker).
 * For an image that is already on the article, use the "Add to Stock"
 * button on the image instead (components/ArticleImageToStock.jsx).
 *
 * Renders nothing when R2 isn't configured.
 *
 * Usage:
 *   const stock = useAddToStock();
 *   <AddToStockOption stock={stock} testIdPrefix="featured" />
 *   // after the article upload succeeds:
 *   stock.stashIfEnabled({ file, article: updated, imageUrl, kind: "featured" });
 */
export function useAddToStock() {
  const qc = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [keywords, setKeywords] = useState("");
  const [credit, setCredit] = useState("");
  const status = useQuery({
    queryKey: ["stock-library-status"],
    queryFn: api.stockLibraryStatus,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const configured = !!status.data?.configured;

  const reset = () => {
    setEnabled(false);
    setKeywords("");
    setCredit("");
  };

  // Fire-and-report: the article upload has already succeeded, so a
  // library failure is a toast, never a failed upload. The fields are
  // captured and the checkbox reset synchronously, so a second upload
  // started straight away is never added by accident.
  // Callers still pass `kind` ("featured" | "body"); it is no longer
  // needed now that the photo goes live at once (only its origin is
  // recorded — the old pending_* fields waited for publish).
  const stashIfEnabled = ({ file, article, imageUrl }) => {
    if (!configured || !enabled || !file || !article?.id || !imageUrl) return;
    const fields = {
      keywords,
      credit,
      article_id: article.id,
      article_image_url: imageUrl,
    };
    reset();
    api
      .uploadStockLibraryPhoto(file, fields)
      .then((res) => {
        qc.invalidateQueries({ queryKey: ["stock-library"] });
        qc.invalidateQueries({ queryKey: ["stock-article-status", article.id] });
        if (res?.duplicate) {
          toast.success("This photo is already in Newsdesk Stock");
        } else {
          toast.success("Added to Newsdesk Stock");
        }
      })
      .catch((e) =>
        toast.error(
          "Image uploaded, but it couldn't be added to Newsdesk Stock: " +
            (e?.response?.data?.detail || e?.message || "unknown error"),
        ),
      );
  };

  return {
    configured, enabled, setEnabled, keywords, setKeywords, credit, setCredit,
    stashIfEnabled,
  };
}

export default function AddToStockOption({ stock, testIdPrefix = "stock", className = "" }) {
  if (!stock?.configured) return null;
  return (
    <div className={"text-xs " + className} data-testid={`${testIdPrefix}-add-to-stock`}>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          data-testid={`${testIdPrefix}-add-to-stock-checkbox`}
          checked={stock.enabled}
          onChange={(e) => stock.setEnabled(e.target.checked)}
        />
        <span>Add this photo to Newsdesk Stock</span>
      </label>
      {stock.enabled && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            data-testid={`${testIdPrefix}-add-to-stock-keywords`}
            value={stock.keywords}
            onChange={(e) => stock.setKeywords(e.target.value)}
            placeholder="Keywords, comma separated (e.g. malecón, ensenada, sunset)"
            className="w-full text-xs px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <input
            data-testid={`${testIdPrefix}-add-to-stock-credit`}
            value={stock.credit}
            onChange={(e) => stock.setCredit(e.target.value)}
            placeholder="Preset caption / credit (e.g. Photo: Oliver Quintero)"
            className="w-full text-xs px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <div className="sm:col-span-2 text-[10px] text-[var(--ink-muted)]">
            Upload or paste the photo now. The full-size file is added to Newsdesk Stock
            straight away. For a photo that is already on the article, use its
            &ldquo;Add to Stock&rdquo; button.
          </div>
        </div>
      )}
    </div>
  );
}
